import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { formatEUR, todayISO, DEFAULT_VAT_RATE } from "@/lib/formatters";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PdfPreviewDialog } from "@/components/PdfPreview";
import { GoogleActions } from "@/components/GoogleActions";
import { DateInput } from "@/components/DateInput";
import { AddressSection } from "@/components/quote/AddressSection";
import { ServicesSection } from "@/components/quote/ServicesSection";
import { StorageSection } from "@/components/quote/StorageSection";
import { PricingSection } from "@/components/quote/PricingSection";
import { RelatedDocsList } from "@/components/quote/RelatedDocsList";
import { useQuoteCalculations } from "@/lib/useDocCalculations";
import { useLocalDraft } from "@/lib/useLocalDraft";
import { LocalDraftBanner, LocalDraftSavedIndicator } from "@/components/LocalDraftBanner";
import { VatModeSelector } from "@/components/VatModeSelector";
import { VAT_MODE_STANDARD, VAT_MODE_SHORT } from "@/lib/vatModes";
import { ArrowLeft, Plus, Trash2, Save, Send, Copy, XCircle, Archive, Package, Receipt, Eye, CheckCircle2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const emptyLine = () => ({ id: "", description: "", quantity: 1, unit_price: 0, sort_order: 0 });
const emptyExtra = () => ({ description: "", amount: 0 });
const emptyMultiItem = () => ({ description: "", price: 0, sub_items: [{ description: "", quantity: 1 }] });

// Intervallo di re-render del banner "Bozza locale salvata N min fa" — serve solo
// per aggiornare il display del tempo relativo, NON è l'intervallo di salvataggio
// (gestito dal debounce interno di useLocalDraft).
const DRAFT_TIMESTAMP_REFRESH_MS = 30_000;

export default function QuoteForm() {
  const { id } = useParams();
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const isNew = !id;
  const canCreate = hasPermission("quotes.create");
  const canEditAll = hasPermission("quotes.edit_all");
  const canIssue = hasPermission("quotes.issue");
  const canVersion = hasPermission("quotes.version");
  const canCancel = hasPermission("quotes.cancel");
  const canArchive = hasPermission("quotes.archive");

  const [form, setForm] = useState({
    header_template_id: "", customer_id: "", date: todayISO(), subject: "",
    pricing_type: "itemized", lump_sum_amount: 0,
    extra_lump_sum_amount: 0, extra_lump_sum_label: "Quotazione forfettaria",
    extra_lump_sum_items: [],
    pickup_address: "", pickup_city: "", pickup_province: "", pickup_cap: "", pickup_date: "",
    pickup_floor: "", pickup_apartment: "", pickup_access_type: "normale",
    delivery_type: "consegna",
    delivery_address: "", delivery_city: "", delivery_province: "", delivery_cap: "", delivery_date: "",
    delivery_floor: "", delivery_apartment: "", delivery_access_type: "normale",
    packing_large: 0, packing_medium: 0, packing_small: 0, packing_clothes: 0,
    packing_household: false, packing_furniture: false, disassembly: false, assembly: false,
    insurance_requested: false, insurance_amount: 0, work_days: 0, materials_cost: 0,
    storage_handling_cost: 0, storage_monthly_cost: 0, storage_months: 0, storage_free_months: 0,
    storage_release_type: "", storage_release_price: 0,
    notes: "", line_items: [emptyLine()], extra_costs: [], multi_items: [],
    deposit: 0, vat_rate: DEFAULT_VAT_RATE,
    vat_mode: VAT_MODE_STANDARD, vat_legal_text: "",
    payment_notes: "", payment_bank_account: "", assigned_to: ""
  });
  const [quote, setQuote] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [movers, setMovers] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [relatedDocs, setRelatedDocs] = useState({ inventories: [], proformas: [] });

  const loadRelatedDocs = useCallback(async (quoteId) => {
    if (!quoteId) return;
    try {
      const { data } = await api.get(`/quotes/${quoteId}/related-docs`);
      setRelatedDocs({ inventories: data.inventories || [], proformas: data.proformas || [] });
    } catch {
      setRelatedDocs({ inventories: [], proformas: [] });
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [custRes, tmplRes, orgRes] = await Promise.all([
        api.get("/customers?limit=200"),
        api.get("/header-templates"),
        api.get("/organization"),
      ]);
      setCustomers(custRes.data.items || []);
      setTemplates(tmplRes.data || []);
      setBankAccounts(orgRes.data?.bank_accounts || []);
      if (canEditAll) {
        const mRes = await api.get("/users");
        setMovers((mRes.data || []).filter(u => u.role === "MOVER" && u.is_active));
      }
      if (id) {
        const { data } = await api.get(`/quotes/${id}`);
        setQuote(data);
        loadRelatedDocs(id);
        setForm({
          header_template_id: data.header_template_id || "", customer_id: data.customer_id || "",
          date: data.date || todayISO(), subject: data.subject || "",
          pricing_type: data.pricing_type || "itemized", lump_sum_amount: data.lump_sum_amount || 0,
          extra_lump_sum_amount: data.extra_lump_sum_amount || 0,
          extra_lump_sum_label: data.extra_lump_sum_label || "Quotazione forfettaria",
          extra_lump_sum_items: data.extra_lump_sum_items || [],
          pickup_address: data.pickup_address || "", pickup_city: data.pickup_city || "",
          pickup_province: data.pickup_province || "", pickup_cap: data.pickup_cap || "",
          pickup_date: data.pickup_date || "", pickup_floor: data.pickup_floor || "",
          pickup_apartment: data.pickup_apartment || "", pickup_access_type: data.pickup_access_type || "normale",
          delivery_type: data.delivery_type || "consegna",
          delivery_address: data.delivery_address || "", delivery_city: data.delivery_city || "",
          delivery_province: data.delivery_province || "", delivery_cap: data.delivery_cap || "",
          delivery_date: data.delivery_date || "", delivery_floor: data.delivery_floor || "",
          delivery_apartment: data.delivery_apartment || "", delivery_access_type: data.delivery_access_type || "normale",
          packing_large: data.packing_large || 0, packing_medium: data.packing_medium || 0,
          packing_small: data.packing_small || 0, packing_clothes: data.packing_clothes || 0,
          packing_household: data.packing_household || false, packing_furniture: data.packing_furniture || false,
          disassembly: data.disassembly || false, assembly: data.assembly || false,
          insurance_requested: data.insurance_requested || false, insurance_amount: data.insurance_amount || 0,
          work_days: data.work_days || 0,
          materials_cost: data.materials_cost || 0,
          storage_handling_cost: data.storage_handling_cost || 0, storage_monthly_cost: data.storage_monthly_cost || 0,
          storage_months: data.storage_months || 0, storage_free_months: data.storage_free_months || 0,
          storage_release_type: data.storage_release_type || "", storage_release_price: data.storage_release_price || 0,
          notes: data.notes || "",
          line_items: data.line_items?.length ? data.line_items : [emptyLine()],
          extra_costs: data.extra_costs || [],
          multi_items: data.multi_items || [],
          deposit: data.deposit || 0, vat_rate: data.vat_rate ?? DEFAULT_VAT_RATE,
          vat_mode: data.vat_mode || VAT_MODE_STANDARD,
          vat_legal_text: data.vat_legal_text || "",
          payment_notes: data.payment_notes || "", payment_bank_account: data.payment_bank_account || "",
          assigned_to: data.assigned_to || ""
        });
      }
    } catch {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  }, [id, canEditAll, loadRelatedDocs]);

  useEffect(() => { loadData(); }, [loadData]);

  const isDraft = !quote || quote.status === "DRAFT";
  const isEditable = isDraft;

  // ---- Persistent local draft (offline / network-loss recovery) ----
  const draftKey = isNew ? "quote:new" : (id ? `quote:${id}` : null);
  const draft = useLocalDraft({
    key: draftKey,
    form,
    enabled: isEditable && !loading,
    serverUpdatedAt: quote?.updated_at || null,
  });
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!draft.savedAt) return;
    const t = setInterval(() => forceTick(x => x + 1), DRAFT_TIMESTAMP_REFRESH_MS);
    return () => clearInterval(t);
  }, [draft.savedAt]);
  const handleRestoreDraft = () => {
    const data = draft.restore();
    if (data) {
      setForm(data);
      toast.success("Bozza locale ripristinata");
    }
  };

  // Line items helpers
  const updateLine = (idx, field, val) => { const items = [...form.line_items]; items[idx] = { ...items[idx], [field]: val }; setForm({ ...form, line_items: items }); };
  const addLine = () => setForm({ ...form, line_items: [...form.line_items, emptyLine()] });
  const removeLine = (idx) => setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) });
  const addExtra = () => setForm({ ...form, extra_costs: [...form.extra_costs, emptyExtra()] });
  const removeExtra = (idx) => setForm({ ...form, extra_costs: form.extra_costs.filter((_, i) => i !== idx) });
  const updateExtra = (idx, field, val) => { const costs = [...form.extra_costs]; costs[idx] = { ...costs[idx], [field]: val }; setForm({ ...form, extra_costs: costs }); };

  // Multi items helpers
  const addMultiItem = () => setForm({ ...form, multi_items: [...form.multi_items, emptyMultiItem()] });
  const removeMultiItem = (idx) => setForm({ ...form, multi_items: form.multi_items.filter((_, i) => i !== idx) });
  const updateMultiItem = (idx, field, val) => { const items = [...form.multi_items]; items[idx] = { ...items[idx], [field]: val }; setForm({ ...form, multi_items: items }); };
  const addSubItem = (miIdx) => { const items = [...form.multi_items]; items[miIdx] = { ...items[miIdx], sub_items: [...items[miIdx].sub_items, { description: "", quantity: 1 }] }; setForm({ ...form, multi_items: items }); };
  const removeSubItem = (miIdx, siIdx) => { const items = [...form.multi_items]; items[miIdx] = { ...items[miIdx], sub_items: items[miIdx].sub_items.filter((_, i) => i !== siIdx) }; setForm({ ...form, multi_items: items }); };
  const updateSubItem = (miIdx, siIdx, field, val) => { const items = [...form.multi_items]; const subs = [...items[miIdx].sub_items]; subs[siIdx] = { ...subs[siIdx], [field]: val }; items[miIdx] = { ...items[miIdx], sub_items: subs }; setForm({ ...form, multi_items: items }); };

  // Totals
  // Calcoli derivati: tutti memoizzati nell'hook useQuoteCalculations
  const calc = useQuoteCalculations(form);
  const {
    isLumpSum, lineSubtotal, multiSubtotal, extraLumpSum, isDeposito,
    subtotal, extraTotal, paidMonths, handlingCost, depositCost, releaseCost,
    storageTotal, materialsCost, totalBeforeVat, vatAmount, insuranceAmount,
    total, balanceDue, vatMode, vatZeroed, visibleMultiItems, visibleExtraCosts, positiveExtraCount,
  } = calc;

  const handleSave = async () => {
    const pickupFields = ['pickup_address', 'pickup_city', 'pickup_province', 'pickup_cap'];
    const deliveryFields = form.delivery_type === "deposito" ? [] : ['delivery_address', 'delivery_city', 'delivery_province', 'delivery_cap'];
    const addrFields = [...pickupFields, ...deliveryFields];
    if (addrFields.some(f => !form[f]?.trim())) {
      toast.error("Compilare tutti i campi degli indirizzi (Indirizzo, Città, Provincia, CAP)");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, line_items: form.line_items.map((li, i) => ({ ...li, sort_order: i })), deposit: parseFloat(form.deposit) || 0, vat_rate: parseFloat(form.vat_rate) || DEFAULT_VAT_RATE };
      if (isNew) {
        const { data } = await api.post("/quotes", payload);
        toast.success("Preventivo creato");
        draft.clear(form);
        navigate(`/quotes/${data.id}`, { replace: true });
      } else {
        const { data } = await api.put(`/quotes/${id}`, payload);
        setQuote(data);
        toast.success("Preventivo aggiornato");
        draft.clear(form);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const handleAction = async (action) => {
    try {
      if (action === "issue") { const { data } = await api.post(`/quotes/${id}/issue`); setQuote(data); toast.success("Preventivo emesso"); }
      else if (action === "version") { const { data } = await api.post(`/quotes/${id}/version`); toast.success("Nuova versione creata"); navigate(`/quotes/${data.id}`, { replace: true }); }
      else if (action === "cancel") { await api.post(`/quotes/${id}/cancel`); toast.success("Preventivo annullato"); loadData(); }
      else if (action === "archive") { await api.post(`/quotes/${id}/archive`); toast.success("Preventivo archiviato"); loadData(); }
      else if (action === "delete") { if (!window.confirm("Eliminare questo preventivo?")) return; await api.delete(`/quotes/${id}`); toast.success("Preventivo eliminato"); navigate("/quotes"); }
      else if (action === "inventory") { const { data } = await api.post(`/inventories/from-quote/${id}`); toast.success("Inventario creato"); navigate(`/inventories/${data.id}`); }
      else if (action === "proforma") {
        const { data } = await api.post(`/proformas/from-quote/${id}`);
        if (data?.type === "paired") {
          toast.success("Create 2 proforme: Deposito + Riconsegna");
          navigate(`/proformas/${data.deposit.id}`);
        } else {
          toast.success("Fattura proforma creata");
          navigate(`/proformas/${data.id}`);
        }
      }
      else if (action === "pdf") { setPdfPreviewUrl(`/quotes/${id}/pdf`); }
      else if (action === "accept") { await api.post(`/quotes/${id}/accept`); toast.success("Preventivo segnato come accettato"); loadData(); }
      else if (action === "unaccept") {
        if (!window.confirm("Rimuovere l'accettazione da questo preventivo?")) return;
        await api.post(`/quotes/${id}/unaccept`); toast.success("Accettazione rimossa"); loadData();
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="quote-form-page" className="space-y-6 animate-fade-in">
      {/* Header + Action Buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/quotes")} className="p-1.5 hover:bg-zinc-100 rounded-sm" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></button>
          <div>
            <h1 className="text-xl font-bold tracking-tighter font-['Outfit']">{isNew ? "Nuovo Preventivo" : (quote?.number || "Preventivo Bozza")}</h1>
            <div className="flex items-center gap-2 flex-wrap">
              {quote && <StatusBadge status={quote.status} />}
              {quote?.accepted_at && (
                <span
                  data-testid="quote-accepted-badge"
                  className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-800"
                  title={`Accettato il ${(() => { const p = quote.accepted_at.slice(0,10).split('-'); return p.length===3 ? `${p[2]}/${p[1]}/${p[0]}` : quote.accepted_at.slice(0,10); })()}`}
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Accettato il {(() => { const p = quote.accepted_at.slice(0,10).split('-'); return p.length===3 ? `${p[2]}/${p[1]}/${p[0]}` : quote.accepted_at.slice(0,10); })()}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 overflow-x-auto pb-1 -mb-1">
          {isEditable && <Button onClick={handleSave} disabled={saving} className="bg-[#002FA7] hover:bg-[#002482] rounded-sm text-xs sm:text-sm" data-testid="save-quote-btn"><Save className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">{saving ? "Salvataggio..." : "Salva"}</span></Button>}
          {!isNew && canIssue && isDraft && <Button onClick={() => handleAction("issue")} variant="outline" className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs sm:text-sm" data-testid="issue-quote-btn"><Send className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Emetti</span></Button>}
          {!isNew && quote?.status === "ISSUED" && <>
            {canEditAll && !quote?.accepted_at && <Button onClick={() => handleAction("accept")} variant="outline" className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs sm:text-sm" data-testid="accept-quote-btn"><CheckCircle2 className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Segna come Accettato</span></Button>}
            {canEditAll && quote?.accepted_at && <Button onClick={() => handleAction("unaccept")} variant="outline" className="rounded-sm text-xs sm:text-sm" data-testid="unaccept-quote-btn"><RotateCcw className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Rimuovi accettazione</span></Button>}
            {canVersion && <Button onClick={() => handleAction("version")} variant="outline" className="rounded-sm text-xs sm:text-sm" data-testid="version-quote-btn"><Copy className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Aggiorna ID</span></Button>}
            {hasPermission("inventories.create") && <Button onClick={() => handleAction("inventory")} variant="outline" className="rounded-sm text-xs sm:text-sm" data-testid="create-inventory-btn"><Package className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Crea Inventario</span></Button>}
            {hasPermission("proformas.create") && <Button onClick={() => handleAction("proforma")} variant="outline" className="rounded-sm text-xs sm:text-sm" data-testid="create-proforma-btn"><Receipt className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Crea Proforma</span></Button>}
          </>}
          {!isNew && <Button onClick={() => handleAction("pdf")} variant="outline" className="rounded-sm text-xs sm:text-sm" data-testid="pdf-quote-btn"><Eye className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Anteprima PDF</span></Button>}
          {!isNew && hasPermission("google.drive") && <GoogleActions docType="quote" docId={id} docNumber={quote?.number} customerEmail={quote?.customer_email} />}
          {!isNew && quote?.status !== "CANCELLED" && quote?.status !== "ARCHIVED" && <>
            {canCancel && <Button onClick={() => handleAction("cancel")} variant="outline" className="rounded-sm text-red-600 hover:bg-red-50 text-xs sm:text-sm" data-testid="cancel-quote-btn"><XCircle className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Annulla</span></Button>}
            {canArchive && <Button onClick={() => handleAction("archive")} variant="outline" className="rounded-sm text-xs sm:text-sm" data-testid="archive-quote-btn"><Archive className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Archivia</span></Button>}
          </>}
          {!isNew && canEditAll && isDraft && <Button onClick={() => handleAction("delete")} variant="outline" className="rounded-sm text-red-600 hover:bg-red-50 text-xs sm:text-sm" data-testid="delete-quote-btn"><Trash2 className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Elimina</span></Button>}
        </div>
      </div>

      {isEditable && <LocalDraftBanner draft={draft} onRestore={handleRestoreDraft} kind="quote" />}
      {isEditable && <LocalDraftSavedIndicator draft={draft} kind="quote" />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Document Info */}
          <div className="card-surface p-5 space-y-4">
            <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Informazioni Documento</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs text-zinc-500">Template Intestazione</Label>
                <Select value={form.header_template_id} onValueChange={(v) => setForm({ ...form, header_template_id: v })} disabled={!isEditable}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-zinc-500">Cliente *</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })} disabled={!isEditable}>
                  <SelectTrigger data-testid="quote-customer-select" className="rounded-sm"><SelectValue placeholder="Seleziona cliente..." /></SelectTrigger>
                  <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name || c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-zinc-500">Data</Label>
                <DateInput data-testid="quote-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} disabled={!isEditable} className="rounded-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-zinc-500">Oggetto</Label>
                <Input data-testid="quote-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} disabled={!isEditable} className="rounded-sm" placeholder="Es: Trasloco appartamento" />
              </div>
            </div>
          </div>

          <AddressSection form={form} setForm={setForm} isEditable={isEditable} />
          <ServicesSection form={form} setForm={setForm} isEditable={isEditable} />
          <PricingSection
            form={form} setForm={setForm} isEditable={isEditable}
            addLine={addLine} removeLine={removeLine} updateLine={updateLine}
            addExtra={addExtra} removeExtra={removeExtra} updateExtra={updateExtra}
            addMultiItem={addMultiItem} removeMultiItem={removeMultiItem} updateMultiItem={updateMultiItem}
            addSubItem={addSubItem} removeSubItem={removeSubItem} updateSubItem={updateSubItem}
            middleSlot={<StorageSection form={form} setForm={setForm} isEditable={isEditable} storageTotal={storageTotal} paidMonths={paidMonths} />}
          />

          {/* Note */}
          <VatModeSelector
            form={form}
            onChange={({ vat_mode, vat_legal_text }) =>
              setForm({ ...form, vat_mode, vat_legal_text })
            }
            disabled={!isEditable}
            testIdPrefix="quote"
          />

          {/* Notes */}
          <div className="card-surface p-5 space-y-4">
            <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Note e Pagamento</h2>
            <textarea data-testid="quote-notes" placeholder="Note..." value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} disabled={!isEditable} className="input-field min-h-[80px] resize-y" rows={3} />
            <textarea data-testid="quote-payment-notes" placeholder="Modalità di pagamento..." value={form.payment_notes} onChange={(e) => setForm({ ...form, payment_notes: e.target.value })} disabled={!isEditable} className="input-field min-h-[60px] resize-y" rows={2} />
            {bankAccounts.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs text-zinc-500">Conto corrente per bonifico</Label>
                <Select value={form.payment_bank_account} onValueChange={(v) => setForm({ ...form, payment_bank_account: v === "none" ? "" : v })} disabled={!isEditable}>
                  <SelectTrigger className="rounded-sm" data-testid="quote-bank-select"><SelectValue placeholder="Seleziona conto..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuno</SelectItem>
                    {bankAccounts.map((ba, idx) => (
                      <SelectItem key={`ba-${ba.iban}`} value={ba.iban}>{ba.bank_name} — {ba.iban}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="card-surface p-5 space-y-3">
            <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Riepilogo</h2>
            <div className="space-y-2 text-sm">
              {!isLumpSum && <>
                {lineSubtotal > 0 && <div className="flex justify-between"><span className="text-zinc-500">Subtotale voci</span><span>{formatEUR(lineSubtotal)}</span></div>}
                {visibleMultiItems.map((mi, i) => (
                  <div key={`mi-sum-${i}`} className="flex justify-between"><span className="text-zinc-500 truncate mr-2">{mi.description}</span><span className="shrink-0">{formatEUR(mi.price)}</span></div>
                ))}
                {extraLumpSum > 0 && (
                  <div className="flex justify-between" data-testid="quote-extra-lump-summary">
                    <span className="text-zinc-500 truncate mr-2">{form.extra_lump_sum_label || "Quotazione forfettaria"}</span>
                    <span className="shrink-0">{formatEUR(extraLumpSum)}</span>
                  </div>
                )}
              </>}
              {isLumpSum && <div className="flex justify-between"><span className="text-zinc-500">Trasloco (prezzo a corpo)</span><span>{formatEUR(subtotal)}</span></div>}
              {visibleExtraCosts.map((ec, i) => (
                <div key={`ec-sum-${i}`} className="flex justify-between"><span className="text-zinc-500 truncate mr-2">{ec.description}</span><span className="shrink-0">{formatEUR(ec.amount)}</span></div>
              ))}
              {extraTotal > 0 && positiveExtraCount > 1 && <div className="flex justify-between text-zinc-400 text-xs"><span>Tot. costi extra</span><span>{formatEUR(extraTotal)}</span></div>}
              {handlingCost > 0 && <div className="flex justify-between text-xs italic" data-testid="quote-handling-summary"><span className="text-zinc-400">Costo Movimentazione (informativo)</span><span className="text-zinc-400">{formatEUR(handlingCost)}</span></div>}
              {materialsCost > 0 && <div className="flex justify-between" data-testid="quote-materials-summary"><span className="text-zinc-500">Costo Materiali</span><span>{formatEUR(materialsCost)}</span></div>}
              {depositCost > 0 && <div className="flex justify-between" data-testid="quote-deposit-summary"><span className="text-zinc-500">Deposito</span><span>{formatEUR(depositCost)}</span></div>}
              {releaseCost > 0 && <div className="flex justify-between" data-testid="quote-release-summary"><span className="text-zinc-500">Prezzo Riconsegna</span><span>{formatEUR(releaseCost)}</span></div>}
              <div className="flex justify-between items-center gap-2">
                <span className="text-zinc-500">IVA</span>
                <div className="flex items-center gap-1">
                  {vatZeroed ? (
                    <span
                      data-testid="quote-vat-zero-label"
                      className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-sm mr-2"
                      title={VAT_MODE_SHORT[vatMode] || "Regime IVA speciale"}
                    >
                      {VAT_MODE_SHORT[vatMode] || "Regime speciale"} — 0%
                    </span>
                  ) : (
                    <>
                      <Input type="number" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: parseFloat(e.target.value) || 0 })} disabled={!isEditable} className="rounded-sm w-16 h-7 text-xs" />
                      <span className="text-zinc-400">%</span>
                    </>
                  )}
                  <span className="ml-2" data-testid="quote-vat-amount">{formatEUR(vatAmount)}</span>
                </div>
              </div>
              {insuranceAmount > 0 && (
                <div className="flex justify-between" data-testid="quote-insurance-summary">
                  <span className="text-zinc-500">Assicurazione <span className="text-zinc-400 text-xs">(esente IVA)</span></span>
                  <span>{formatEUR(insuranceAmount)}</span>
                </div>
              )}
              <div className="border-t border-zinc-200 pt-2 flex justify-between font-semibold"><span>Totale</span><span className="text-[#002FA7]">{formatEUR(total)}</span></div>
              <div className="flex justify-between items-center gap-2">
                <span className="text-zinc-500">Caparra</span>
                <Input type="number" placeholder="0 €" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: parseFloat(e.target.value) || 0 })} disabled={!isEditable} className="rounded-sm w-28 h-7 text-xs text-right" />
              </div>
              {(form.deposit || 0) > 0 && <div className="flex justify-between font-semibold text-emerald-700"><span>Saldo</span><span>{formatEUR(balanceDue)}</span></div>}
            </div>
          </div>
          {canEditAll && (
            <div className="card-surface p-5 space-y-3">
              <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Assegnazione</h2>
              <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v === "none" ? "" : v })} disabled={!isEditable}>
                <SelectTrigger className="rounded-sm"><SelectValue placeholder="Nessun operatore" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Nessun operatore</SelectItem>{movers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {!isNew && (relatedDocs.inventories.length > 0 || relatedDocs.proformas.length > 0) && (
            <div className="card-surface p-5 space-y-3" data-testid="related-docs-card">
              <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Documenti Collegati</h2>
              <RelatedDocsList relatedDocs={relatedDocs} navigate={navigate} />
            </div>
          )}
          {quote?.versions && quote.versions.length > 1 && (
            <div className="card-surface p-5 space-y-3">
              <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Versioni</h2>
              <div className="space-y-2">
                {quote.versions.map(v => (
                  <button key={v.id} onClick={() => navigate(`/quotes/${v.id}`)} className={`w-full text-left px-3 py-2 rounded-sm text-sm border transition-colors ${v.id === id ? "border-[#002FA7] bg-blue-50/50" : "border-zinc-200 hover:bg-zinc-50"}`}>
                    <span className="font-mono">{v.number || "BOZZA"}</span> <StatusBadge status={v.status} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <PdfPreviewDialog pdfEndpoint={pdfPreviewUrl} open={!!pdfPreviewUrl} onClose={() => setPdfPreviewUrl(null)} filename={`${quote?.number || "preventivo"}.pdf`} />
    </div>
  );
}
